// src/pages/contentManagement/devotions/devotionList.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { Button, Collapse, Divider, Form, Input, List, Modal, Pagination, Spin } from "antd";
import { FaEdit, FaTrash, FaChevronDown } from "react-icons/fa";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import Layout from "../../../components/layout/layout";
import TableLayout from "../../../components/tableLayout/tableLayout";
import TopToolBar from "../../../components/topToolBar/topToolBar";
import "./devotionList.css";

const { TextArea } = Input;
const { Panel } = Collapse;

// Initial state for useReducer
const initialState = {
  isLoading: true,
  searchTerm: "",
  filters: {},
  devotions: [],
  currentPage: 1,
  pageSize: 5,
  sortConfig: { key: "createdAt", direction: "desc" },
  showDeleteModal: false,
  deleteDevotionId: null,
  editingDevotion: null
};

// Reducer for state management
const devotionReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setSearchTerm":
      return { ...state, searchTerm: action.payload, currentPage: 1 };
    case "setFilters":
      return { ...state, filters: action.payload, currentPage: 1 };
    case "setDevotions":
      return { ...state, devotions: action.payload };
    case "setPagination":
      return {
        ...state,
        currentPage: action.payload.page,
        pageSize: action.payload.pageSize
      };
    case "setSortConfig":
      return { ...state, sortConfig: action.payload };
    case "setDeleteModal":
      return {
        ...state,
        showDeleteModal: action.payload.show,
        deleteDevotionId: action.payload.devotionId || null
      };
    case "setEditingDevotion":
      return { ...state, editingDevotion: action.payload };
    case "removeDevotion":
      return {
        ...state,
        devotions: state.devotions.filter((devotion) => devotion.id !== action.payload)
      };
    case "addDevotion":
      return {
        ...state,
        devotions: [action.payload, ...state.devotions]
      };
    case "updateDevotion":
      return {
        ...state,
        devotions: state.devotions.map((devotion) => (devotion.id === action.payload.id ? action.payload : devotion))
      };
    case "resetFilters":
      return {
        ...state,
        filters: {},
        searchTerm: "",
        currentPage: 1
      };
    default:
      return state;
  }
};

function DevotionManagement() {
  const [state, dispatch] = useReducer(devotionReducer, initialState);
  const [form] = Form.useForm();

  const [submitLoading, setSubmitLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "success"
  });

  const { isLoading, searchTerm, filters, devotions, currentPage, pageSize, sortConfig, showDeleteModal, deleteDevotionId, editingDevotion } = state;

  const showNotification = useCallback((message, type = "success") => {
    // Reset first, then set — guarantees show flips false → true every time
    setNotification({ show: false, message, type });
    setTimeout(() => {
      setNotification({ show: true, message, type });
    }, 0);
  }, []);

  const hideNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, show: false }));
  }, []);

  const filtersConfig = useMemo(
    () => ({
      sortOrder: {
        label: "Sort By",
        options: [
          { value: "newest", label: "Newest First" },
          { value: "oldest", label: "Oldest First" }
        ]
      }
    }),
    []
  );

  const globalSearch = useCallback((items, term) => {
    if (!term?.trim()) return items;

    const searchLower = term.toLowerCase().trim();

    return items.filter((item) => {
      const searchFields = [item.verse || "", item.content || ""];

      return searchFields.some((field) => field.toLowerCase().includes(searchLower));
    });
  }, []);

  const filteredData = useMemo(() => {
    let result = [...devotions];

    if (filters.sortOrder) {
      switch (filters.sortOrder) {
        case "newest":
          result.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          break;
        case "oldest":
          result.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0));
          break;
        default:
          break;
      }
    } else if (sortConfig) {
      result.sort((a, b) => {
        const { key, direction } = sortConfig;
        if (key === "createdAt") {
          return direction === "asc" ? new Date(a.createdAt || 0) - new Date(b.createdAt || 0) : new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        }
        const valA = a[key]?.toString().toLowerCase() || "";
        const valB = b[key]?.toString().toLowerCase() || "";
        if (valA < valB) return direction === "asc" ? -1 : 1;
        if (valA > valB) return direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    result = globalSearch(result, searchTerm);

    return result;
  }, [devotions, filters, searchTerm, globalSearch, sortConfig]);

  const currentPageData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, pageSize]);

  const fetchDevotions = useCallback(async () => {
    dispatch({ type: "setLoading", payload: true });
    try {
      const token = await getAuthToken();
      const response = await axios.get(API_ENDPOINTS.DEVOTION, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200 && Array.isArray(response.data)) {
        const devotionsData = response.data.map((dev) => ({
          id: dev.id,
          verse: dev.bibleVerse,
          content: dev.devotionText,
          createdAt: dev.createdAt || new Date().toISOString()
        }));
        dispatch({ type: "setDevotions", payload: devotionsData });
      } else {
        throw new Error("Unexpected API response format.");
      }
    } catch (error) {
      console.error("Error fetching devotions:", error);
      showNotification("Failed to load devotions", "error");
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [showNotification]);

  const handleSubmit = useCallback(
    async (values) => {
      setSubmitLoading(true);

      const devotionData = {
        bibleVerse: values.verse,
        devotionText: values.content
      };

      try {
        const token = await getAuthToken();

        if (editingDevotion) {
          await axios.patch(`${API_ENDPOINTS.DEVOTION}/${editingDevotion.id}`, devotionData, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          });
          showNotification("Devotion updated successfully!", "success");
        } else {
          await axios.post(API_ENDPOINTS.DEVOTION, devotionData, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`
            }
          });
          showNotification("Devotion added successfully!", "success");
        }

        form.resetFields();
        dispatch({ type: "setEditingDevotion", payload: null });
        await fetchDevotions();
      } catch (error) {
        console.error("Error saving devotion:", error.response?.data || error.message);
        showNotification(error.response?.data?.message || "Failed to save devotion. Please try again.", "error");
      } finally {
        setSubmitLoading(false);
      }
    },
    [editingDevotion, fetchDevotions, showNotification, form]
  );

  const handleEdit = useCallback(
    (devotion) => {
      dispatch({ type: "setEditingDevotion", payload: devotion });
      form.setFieldsValue({
        verse: devotion.verse,
        content: devotion.content
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [form]
  );

  const handleCancel = useCallback(() => {
    form.resetFields();
    dispatch({ type: "setEditingDevotion", payload: null });
    showNotification("Form cleared.", "info");
  }, [form, showNotification]);

  const confirmDelete = useCallback((id) => {
    dispatch({
      type: "setDeleteModal",
      payload: { show: true, devotionId: id }
    });
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteDevotionId) return;

    dispatch({ type: "setDeleteModal", payload: { show: false } });
    dispatch({ type: "setLoading", payload: true });

    try {
      const token = await getAuthToken();
      await axios.delete(API_ENDPOINTS.DEVOTION, {
        data: { id: deleteDevotionId },
        headers: { Authorization: `Bearer ${token}` }
      });

      dispatch({ type: "removeDevotion", payload: deleteDevotionId });
      showNotification("Devotion deleted successfully.", "success");
    } catch (error) {
      console.error("Error deleting devotion:", error);
      showNotification("Failed to delete devotion.", "error");
    } finally {
      dispatch({ type: "setLoading", payload: false });
    }
  }, [deleteDevotionId, showNotification]);

  const handleDeleteModalCancel = useCallback(() => {
    dispatch({ type: "setDeleteModal", payload: { show: false } });
  }, []);

  const handleSearch = useCallback((term) => {
    dispatch({ type: "setSearchTerm", payload: term });
  }, []);

  const handleFilterChange = useCallback((newFilters) => {
    dispatch({ type: "setFilters", payload: newFilters });
  }, []);

  const handleReset = useCallback(() => {
    dispatch({ type: "resetFilters" });
  }, []);

  const handlePaginationChange = useCallback((page, size) => {
    dispatch({ type: "setPagination", payload: { page, pageSize: size } });
  }, []);

  const prepareExcelData = useCallback(() => {
    return filteredData.map((devotion, index) => ({
      "SL. NO.": index + 1,
      "Bible Verse": devotion.verse,
      Content: devotion.content
    }));
  }, [filteredData]);

  useEffect(() => {
    fetchDevotions();
  }, [fetchDevotions]);

  return (
    <Layout
      navTitle="Content Management"
      pageTitle="Devotions"
      notificationProps={[
        {
          show: notification.show,
          message: notification.message,
          type: notification.type,
          onClose: hideNotification,
          duration: 4000
        }
      ]}
    >
      <div className="devm-upload-section">
        <Collapse
          expandIcon={({ isActive }) => (
            <FaChevronDown
              style={{
                transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.3s"
              }}
            />
          )}
          className="devm-upload-collapse"
        >
          <Panel header={editingDevotion ? "Edit Devotion" : "Add New Devotion"} key="1">
            <Form form={form} layout="vertical" onFinish={handleSubmit} className="devm-upload-form">
              <Form.Item label="Bible Verse:" name="verse" rules={[{ required: true, message: "Please enter Bible verse" }]}>
                <Input className="devm-upload-input" placeholder="e.g., John 3:16" />
              </Form.Item>

              <Form.Item
                label="Devotion Content:"
                name="content"
                rules={[
                  {
                    required: true,
                    message: "Please enter devotion content"
                  }
                ]}
              >
                <TextArea className="devm-upload-input" placeholder="Enter devotion content" rows={6} />
              </Form.Item>

              <Form.Item>
                <div className="devm-form-btn-group">
                  <Button className="devm-form-cancel-btn" onClick={handleCancel} disabled={submitLoading}>
                    Clear
                  </Button>
                  <Button className="devm-form-submit-btn" htmlType="submit" loading={submitLoading} disabled={submitLoading}>
                    {editingDevotion ? "Update Devotion" : "Upload Devotion"}
                  </Button>
                </div>
              </Form.Item>
            </Form>
          </Panel>
        </Collapse>
      </div>

      <Divider />

      <TableLayout
        toolbar={
          <TopToolBar
            filtersConfig={filtersConfig}
            searchPlaceholder="Search devotions..."
            searchTerm={searchTerm}
            filters={filters}
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            showExcelDownload={true}
            excelData={prepareExcelData()}
            excelFilename="devotions"
            excelSheetName="Devotions"
          />
        }
      >
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin />
          </div>
        ) : (
          <List
            className="tpl-list"
            dataSource={currentPageData}
            locale={{ emptyText: "No devotions available." }}
            renderItem={(devotion) => (
              <List.Item key={devotion.id} className="tpl-list-item">
                <h3>{devotion.verse}</h3>
                <p>{devotion.content}</p>
                <div className="devm-list-btn-group">
                  <Button className="devm-list-edit-btn" icon={<FaEdit />} onClick={() => handleEdit(devotion)}>
                    Edit
                  </Button>
                  <Button className="devm-list-delete-btn" icon={<FaTrash />} onClick={() => confirmDelete(devotion.id)}>
                    Delete
                  </Button>
                </div>
              </List.Item>
            )}
          />
        )}
        <div className="tpl-pagination-wrapper">
          <Pagination className="tpl-pagination" current={currentPage} pageSize={pageSize} total={filteredData.length} onChange={handlePaginationChange} showSizeChanger={false} />
        </div>
      </TableLayout>

      <Modal
        title="Confirm Deletion"
        open={showDeleteModal}
        onCancel={handleDeleteModalCancel}
        footer={[
          <div className="devm-modal-btn-group" key="modal-buttons">
            <Button className="devm-modal-cancel-btn" key="cancel" onClick={handleDeleteModalCancel}>
              Cancel
            </Button>
            <Button className="devm-modal-delete-btn" key="delete" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        ]}
        width={400}
        centered
      >
        <p>Are you sure you want to delete this devotion?</p>
      </Modal>
    </Layout>
  );
}

export default DevotionManagement;
